import { httpRequest } from "../utils/httpClient";
import { getConfig } from "../config";
import { INTEGRATION_TYPE } from "../utils/constant";
import { ApacuanaAPIError } from "../errors";
import ApacuanaSuccess from "../success";

/** @typedef {import("../types/revocations").RequestRevocationResponse} RequestRevocationResponse */
/** @typedef {import("../types/revocations").GetRevocationReasonsResponse} GetRevocationReasonsResponse */

const requestRevocationOnBoarding = async (params) => {
  try {
    const response = await httpRequest(
      "services/api/onboardingclient/requestcert",
      { reason: params.reasonCode },
      "POST"
    );
    return new ApacuanaSuccess(response);
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new Error(`Failed to request revocation: ${error.message}`);
  }
};

const requestRevocationOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Requesting revocation is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Solicita la revocación de un certificado.
 * @param {object} params - Parámetros para la solicitud.
 * @param {number} params.reasonCode - El código de la razón de la revocación.
 * @returns {Promise<RequestRevocationResponse>} Una promesa que resuelve con la confirmación de la solicitud.
 * @throws {Error} Si los parámetros son inválidos.
 */
export const requestRevocation = async (params) => {
  if (!params || typeof params.reasonCode !== "number") {
    throw new Error(
      'The "params" object with a numeric "reasonCode" property is required.'
    );
  }

  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return requestRevocationOnBoarding(params);
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return requestRevocationOnPremise(params);
  }
  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};

const getRevocationReasonsOnBoarding = async () => {
  try {
    const response = await httpRequest(
      "config/api/revocation/reasonsonboarding",
      {},
      "GET"
    );
    if (!response || !response.records) {
      throw new ApacuanaAPIError("Failed to fetch revocation reasons.");
    }
    return new ApacuanaSuccess({ reasons: response.records });
  } catch (error) {
    if (error instanceof ApacuanaAPIError) {
      throw error;
    }
    throw new Error(`Failed to get revocation reasons: ${error.message}`);
  }
};

const getRevocationReasonsOnPremise = async () => {
  throw new ApacuanaAPIError(
    "Getting revocation reasons is not supported for integration type: ONPREMISE",
    501,
    "NOT_IMPLEMENTED"
  );
};

/**
 * Obtiene la lista de razones de revocación de certificados.
 * @returns {Promise<GetRevocationReasonsResponse>} Una promesa que resuelve con la lista de razones de revocación.
 * @throws {ApacuanaAPIError} Si la llamada a la API falla o el tipo de integración no es soportado.
 */
export const getRevocationReasons = async () => {
  const { integrationType } = getConfig();

  if (integrationType === INTEGRATION_TYPE.ONBOARDING) {
    return getRevocationReasonsOnBoarding();
  }
  if (integrationType === INTEGRATION_TYPE.ONPREMISE) {
    return getRevocationReasonsOnPremise();
  }
  throw new ApacuanaAPIError(
    `Unsupported integration type: ${integrationType}`,
    400,
    "UNSUPPORTED_INTEGRATION_TYPE"
  );
};
